// Renders a public image with AVIF and WebP <source> elements when those formats are available.
// Format availability is determined by the virtual:image-hashes map, which only contains entries
// for files that were physically produced during the build. In dev mode the map is empty,
// so this degrades to a plain <img> with no extra network requests for missing formats.

import * as React from "react";
import hashes from "virtual:image-hashes";

function stripQuery(url: string): string {
    const i = url.indexOf("?");
    return i === -1 ? url : url.slice(0, i);
}

/** Returns `path?v=<hash>` if the file exists in the hash map, otherwise null. */
function hashedUrl(path: string): string | null {
    const hash = (hashes as Record<string, string>)[path];
    return hash ? `${path}?v=${hash}` : null;
}

function toFormat(url: string, ext: string): string | null {
    const path = stripQuery(url).replace(/\.(png|jpe?g|webp)$/i, ext);
    return hashedUrl(path);
}

interface PublicImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
    src: string;
}

/**
 * Drop-in replacement for `<img>` that wraps public images in a `<picture>` element
 * offering AVIF and WebP sources when available, falling back to the original format.
 */
export function PublicImage({ src, ...imgProps }: PublicImageProps): JSX.Element {
    const avifSrc = toFormat(src, ".avif");
    const webpSrc = toFormat(src, ".webp");

    if (!avifSrc && !webpSrc) {
        return <img src={src} {...imgProps} />;
    }

    return (
        <picture>
            {avifSrc && <source srcSet={avifSrc} type="image/avif" />}
            {webpSrc && <source srcSet={webpSrc} type="image/webp" />}
            <img src={src} {...imgProps} />
        </picture>
    );
}
